import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {CaretDown} from '../../icons';

const MENU_GAP = 6;
const MENU_MARGIN = 8;
const MENU_WIDTH = 300;

/**
 * Un chip de la frase que abre su menú. El menú va PORTEADO a `body` y `fixed` (receta de
 * `MetricCombobox`): dibujado como hijo lo recortaría cualquier contenedor con `overflow` y lo
 * taparía cualquier ancestro con stacking context. Anclado a la ventana, se REUBICA al scrollear y al
 * cambiar el tamaño, como `MetricCombobox`: cerrarlo ahí lo cerraba apenas se abría en un teléfono,
 * porque enfocar el buscador abre el teclado y eso es un `resize` (C-ac-fe-centro-19). Se cierra con
 * Escape (el foco vuelve al chip) y con un `mousedown` afuera — que corre ANTES del `click`, así que
 * el menú cuenta como adentro.
 *
 * `children({close})`: el contenido; recibe `close` para cerrar al elegir.
 * `mark`: «En tus palabras» (F4), `'ai'` o `'ask'` → clase `-nl-ai` / `-nl-ask` del chip. Va como clase y
 * no como envoltorio: quitar la marca no puede remontar el chip y cerrar su menú abierto.
 * `buttonClassName`: la caja del chip. Por defecto la de la frase; «Por regla» de «Ahora» (F3) usa la de
 * los filtros de la bandeja (`cav-chip`).
 */
export function ChipMenu({label, empty = false, error = false, menuLabel, slot, disabled = false, mark = null,
                             buttonClassName = 'alc-chip -button', children}) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    const close = useCallback(({focusChip = false} = {}) => {
        setOpen(false);
        setPosition(null);
        if (focusChip && buttonRef.current) buttonRef.current.focus();
    }, []);

    const place = useCallback(() => {
        const button = buttonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth || 0;
        const width = Math.min(MENU_WIDTH, Math.max(0, viewportWidth - 2 * MENU_MARGIN));
        const left = Math.max(MENU_MARGIN, Math.min(rect.left, viewportWidth - width - MENU_MARGIN));
        const next = {top: rect.bottom + MENU_GAP, left, width};
        // Sin cambios, el mismo objeto: así reubicar en cada render no vuelve a renderizar.
        setPosition((current) => (current && current.top === next.top && current.left === next.left
            && current.width === next.width ? current : next));
    }, []);

    // En cada render con el menú abierto, no sólo al abrir: lo de arriba puede cambiar de alto mientras
    // tanto (el panel de «En tus palabras» se achica al resolver una aclaración) y el menú, `fixed`, quedaba
    // despegado de su chip (QA-VIS-nl-01).
    useLayoutEffect(() => {
        if (open) place();
    });

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (event) => {
            const inside = (menuRef.current && menuRef.current.contains(event.target))
                || (buttonRef.current && buttonRef.current.contains(event.target));
            if (!inside) close();
        };
        const onKey = (event) => {
            if (event.key === 'Escape') close({focusChip: true});
        };
        const onMove = (event) => {
            // El scroll de adentro del menú (una lista larga de clientes) no lo mueve.
            if (menuRef.current && event && event.target instanceof Node && menuRef.current.contains(event.target)) return;
            place();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onMove, {passive: true, capture: true});
        window.addEventListener('resize', onMove, {passive: true});
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onMove, {capture: true});
            window.removeEventListener('resize', onMove);
        };
    }, [open, close, place]);

    // El foco entra al menú cuando ya está dibujado (un render después de abrir: espera la posición).
    const focusMenu = useCallback((node) => {
        menuRef.current = node;
        if (!node) return;
        const first = node.querySelector('[data-autofocus], input, button:not([disabled])');
        // Sin `preventScroll`, enfocar puede scrollear la página, y el scroll cierra el menú.
        if (first) first.focus({preventScroll: true});
    }, []);

    return (
        <>
            <button type="button" ref={buttonRef} data-slot={slot} disabled={disabled}
                    className={`${buttonClassName}${empty ? ' -empty' : ''}${error ? ' -error' : ''}${open ? ' -open' : ''}${mark ? ` -nl-${mark}` : ''}`}
                    aria-haspopup="dialog" aria-expanded={open}
                    onClick={() => (open ? close() : setOpen(true))}>
                <span className="alc-chip__text">{label}</span>
                <CaretDown size={11} weight="bold" aria-hidden="true"/>
            </button>
            {open && position && createPortal(
                <div className="alc-chip-menu" role="dialog" aria-label={menuLabel || label} ref={focusMenu}
                     data-slot={slot} style={{top: position.top, left: position.left, width: position.width}}>
                    {children({close})}
                </div>,
                document.body,
            )}
        </>
    );
}

/** Las opciones de un chip de elección única. */
export function ChipOptions({options, value, onChoose, close}) {
    return (
        <ul className="alc-chip-menu__options">
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <li key={option.value}>
                        <button type="button" className={`alc-chip-menu__option${selected ? ' -on' : ''}`}
                                aria-pressed={selected} disabled={option.disabled}
                                onClick={() => { onChoose(option.value); close({focusChip: true}); }}>
                            <span className="alc-chip-menu__option-label">{option.label}</span>
                            {option.hint && <span className="alc-chip-menu__option-hint">{option.hint}</span>}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
